/* ── Candidate registration form (PDF) ───────────────────────────────────────
 * Builds an A4 registration form from the candidate's profile and every
 * position they've applied for, and downloads it as a real .pdf file.
 *
 * Runs entirely client-side — no extra endpoint needed.
 * ───────────────────────────────────────────────────────────────────────────── */
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import api from "./api";

/* ── Palette ──────────────────────────────────────────────────────────────── */
const C = {
  navy: [15, 23, 42],
  navySoft: [30, 41, 59],
  blue: [37, 99, 235],
  blueTint: [239, 246, 255],
  text: [51, 65, 85],
  muted: [148, 163, 184],
  line: [226, 232, 240],
  zebra: [248, 250, 252],
  white: [255, 255, 255],
};

const STATUS_COLORS = {
  pending: { bg: [254, 243, 199], fg: [146, 64, 14] },
  reviewed: { bg: [219, 234, 254], fg: [30, 64, 175] },
  accepted: { bg: [209, 250, 229], fg: [6, 95, 70] },
  rejected: { bg: [254, 226, 226], fg: [153, 27, 27] },
};

/* ── Page geometry (mm) ───────────────────────────────────────────────────── */
const PAGE_W = 210;
const PAGE_H = 297;
const M = 15; // left / right margin
const CONTENT_W = PAGE_W - M * 2;
const BOTTOM = PAGE_H - 20; // keep clear of the footer

const DASH = "—";
const val = (v) => (v ? String(v) : DASH);

const fmtDate = (d) =>
  d
    ? new Date(d).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
    : DASH;

const fmtDateTime = (d) =>
  new Date(d).toLocaleString("en-GB", { dateStyle: "long", timeStyle: "short" });

const titleCase = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : DASH);

// Short, human-readable reference: QL-<last 6 of user id>-<yymmdd>
const refNumber = (user) => {
  const id = String(user?._id || user?.id || "")
    .slice(-6)
    .toUpperCase();
  const d = new Date();
  const stamp = `${String(d.getFullYear()).slice(2)}${String(d.getMonth() + 1).padStart(2, "0")}${String(d.getDate()).padStart(2, "0")}`;
  return `QL-${id || "GUEST"}-${stamp}`;
};

const safeFileName = (name) =>
  String(name || "candidate")
    .replace(/[^a-z0-9]+/gi, "_")
    .replace(/^_|_$/g, "") || "candidate";

/* ── Drawing helpers ──────────────────────────────────────────────────────── */
function drawHeader(doc, user, ref) {
  doc.setFillColor(...C.navy);
  doc.rect(0, 0, PAGE_W, 26, "F");
  doc.setFillColor(...C.blue);
  doc.rect(0, 26, PAGE_W, 1.2, "F");

  doc.setTextColor(...C.white);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(17);
  doc.text("Quantum", M, 13);
  const w = doc.getTextWidth("Quantum");
  doc.setTextColor(96, 165, 250);
  doc.text("Logics", M + w, 13);

  doc.setTextColor(148, 163, 184);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(7.5);
  doc.text("CAREERS & RECRUITMENT", M, 19);

  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.setTextColor(...C.white);
  doc.text(ref, PAGE_W - M, 12, { align: "right" });
  doc.setFont("helvetica", "normal");
  doc.setFontSize(7.5);
  doc.setTextColor(148, 163, 184);
  doc.text(`Generated ${fmtDateTime(new Date())}`, PAGE_W - M, 17.5, {
    align: "right",
  });
}

function drawFooter(doc, ref) {
  const pages = doc.getNumberOfPages();
  for (let p = 1; p <= pages; p++) {
    doc.setPage(p);
    doc.setDrawColor(...C.line);
    doc.setLineWidth(0.2);
    doc.line(M, PAGE_H - 14, PAGE_W - M, PAGE_H - 14);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7.5);
    doc.setTextColor(...C.muted);
    doc.text("QuantumLogics — Candidate Registration Form", M, PAGE_H - 9.5);
    doc.text(`${ref}   ·   Page ${p} of ${pages}`, PAGE_W - M, PAGE_H - 9.5, {
      align: "right",
    });
  }
}

/* ── Cursor-aware section label ───────────────────────────────────────────── */
function makeCursor(doc, ref) {
  let y = 34;

  const newPage = () => {
    doc.addPage();
    drawHeader(doc, null, ref);
    y = 34;
  };

  return {
    get y() {
      return y;
    },
    set y(v) {
      y = v;
    },
    space(h) {
      if (y + h > BOTTOM) newPage();
    },
    gap(h) {
      y += h;
    },
    sectionLabel(text) {
      this.space(12);
      doc.setFillColor(...C.blueTint);
      doc.rect(M, y, CONTENT_W, 7, "F");
      doc.setFillColor(...C.blue);
      doc.rect(M, y, 1.2, 7, "F");
      doc.setFont("helvetica", "bold");
      doc.setFontSize(8.5);
      doc.setTextColor(30, 64, 175);
      doc.text(text.toUpperCase(), M + 4, y + 4.8);
      y += 10;
    },
    paragraph(text, { size = 9, color = C.text, lineH = 4.4 } = {}) {
      doc.setFont("helvetica", "normal");
      doc.setFontSize(size);
      doc.setTextColor(...color);
      const lines = doc.splitTextToSize(text, CONTENT_W);
      lines.forEach((line) => {
        this.space(lineH);
        doc.text(line, M, y + 3);
        y += lineH;
      });
    },
    // Bordered box of wrapped text (cover letter) — splits across pages cleanly.
    box(text, { empty = false } = {}) {
      const padX = 3.5;
      const lineH = 4.3;
      doc.setFont("helvetica", empty ? "italic" : "normal");
      doc.setFontSize(9);
      const lines = doc.splitTextToSize(text, CONTENT_W - padX * 2);
      let i = 0;
      while (i < lines.length) {
        // Never start a box with less than three lines of room left.
        this.space(lineH * 3 + 5);
        const top = y;
        const avail = Math.max(1, Math.floor((BOTTOM - top - 5) / lineH));
        const chunk = lines.slice(i, i + avail);
        const h = Math.max(chunk.length * lineH + 5, 12);
        doc.setDrawColor(...C.line);
        doc.setLineWidth(0.2);
        doc.rect(M, top, CONTENT_W, h, "S");
        doc.setTextColor(...(empty ? C.muted : C.text));
        for (let k = 0; k < chunk.length; k++) {
          doc.text(chunk[k], M + padX, top + 5.5 + k * lineH);
        }
        y = top + h;
        i += chunk.length;
      }
    },
    signatureRow() {
      this.space(24);
      const colW = (CONTENT_W - 20) / 2;
      const lineY = y + 12;
      doc.setDrawColor(...C.navy);
      doc.setLineWidth(0.3);
      doc.line(M, lineY, M + colW, lineY);
      doc.line(M + colW + 20, lineY, M + CONTENT_W, lineY);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(7.5);
      doc.setTextColor(...C.muted);
      doc.text("CANDIDATE SIGNATURE", M, lineY + 4);
      doc.text("DATE", M + colW + 20, lineY + 4);
      y = lineY + 8;
    },
  };
}

/* ── Document ─────────────────────────────────────────────────────────────── */
function buildPDF({ user, applications, jobs }) {
  const apps = [...applications].sort(
    (a, b) => new Date(a.appliedAt || 0) - new Date(b.appliedAt || 0),
  );
  const jobById = new Map((jobs || []).map((j) => [String(j._id), j]));
  const multi = apps.length > 1;
  const ref = refNumber(user);

  // Details captured on the application form — take the most recent non-empty
  // value, since every application shares one candidate profile.
  const latest = (key) => {
    for (let i = apps.length - 1; i >= 0; i--)
      if (apps[i][key]) return apps[i][key];
    return "";
  };

  const doc = new jsPDF({ unit: "mm", format: "a4" });
  doc.setProperties({
    title: `QuantumLogics Registration Form — ${user?.name || "Candidate"}`,
    subject: "Candidate Registration Form",
    author: "QuantumLogics",
    creator: "QuantumLogics Careers",
  });

  drawHeader(doc, user, ref);
  const cur = makeCursor(doc, ref);

  /* Title */
  doc.setFont("helvetica", "bold");
  doc.setFontSize(13);
  doc.setTextColor(...C.navy);
  doc.text("CANDIDATE REGISTRATION FORM", PAGE_W / 2, cur.y + 3, {
    align: "center",
  });
  cur.gap(8);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(...C.muted);
  doc.text(
    `Record of the application${multi ? "s" : ""} submitted to QuantumLogics. Please review, sign, and retain a copy.`,
    PAGE_W / 2,
    cur.y,
    { align: "center" },
  );
  cur.gap(6);

  /* Section A — Personal details */
  cur.sectionLabel("Section A — Personal Details");
  autoTable(doc, {
    startY: cur.y,
    margin: { top: 34, left: M, right: M, bottom: 20 },
    // Re-stamp the brand band on any page the table spills onto.
    didDrawPage: () => drawHeader(doc, user, ref),
    theme: "grid",
    styles: {
      font: "helvetica",
      fontSize: 9,
      cellPadding: 2.4,
      lineColor: C.line,
      lineWidth: 0.2,
      textColor: C.text,
      overflow: "linebreak",
    },
    columnStyles: {
      0: {
        cellWidth: 52,
        fillColor: C.zebra,
        fontStyle: "bold",
        fontSize: 8,
        textColor: [71, 85, 105],
      },
      1: { cellWidth: CONTENT_W - 52 },
    },

    body: [
      ["FULL NAME", val(user?.name)],
      ["EMAIL ADDRESS", val(user?.email)],
      ["PHONE NUMBER", val(latest("phone"))],
      ["YEARS OF EXPERIENCE", val(latest("experience"))],
      ["LINKEDIN PROFILE", val(latest("linkedin"))],
      ["PORTFOLIO / GITHUB", val(latest("portfolio"))],
    ],
  });
  cur.y = doc.lastAutoTable.finalY + 4;

  /* Section B — Positions applied for */
  cur.sectionLabel(
    `Section B — Position${multi ? "s" : ""} Applied For (${apps.length})`,
  );

  if (apps.length === 0) {
    cur.box("No applications on record.", { empty: true });
  } else {
    autoTable(doc, {
      startY: cur.y,
      margin: { top: 34, left: M, right: M, bottom: 20 },
      didDrawPage: () => drawHeader(doc, user, ref),
      theme: "grid",
      head: [
        ["#", "Position", "Department", "Type", "Location", "Applied", "Status"],
      ],
      body: apps.map((a, i) => {
        const job = jobById.get(String(a.jobId));
        return [
          String(i + 1),
          val(a.jobTitle),
          val(job?.department),
          val(job?.type),
          val(job?.location),
          fmtDate(a.appliedAt),
          titleCase(a.status),
        ];
      }),
      headStyles: {
        fillColor: C.navy,
        textColor: C.white,
        fontSize: 7.5,
        fontStyle: "bold",
        cellPadding: 2.2,
        lineColor: C.navy,
        lineWidth: 0.2,
      },
      styles: {
        font: "helvetica",
        fontSize: 8,
        cellPadding: 2.2,
        lineColor: C.line,
        lineWidth: 0.2,
        textColor: C.text,
        overflow: "linebreak",
      },
      alternateRowStyles: { fillColor: C.zebra },
      columnStyles: {
        0: { cellWidth: 8, halign: "center", textColor: C.muted },
        1: { cellWidth: 42, fontStyle: "bold", textColor: C.navy },
        2: { cellWidth: 26 },
        3: { cellWidth: 22 },
        4: { cellWidth: 30 },
        5: { cellWidth: 22 },
        6: { cellWidth: 30, halign: "center", fontStyle: "bold" },
      },
      // Status column gets its own badge colouring.
      didParseCell: (data) => {
        if (data.section !== "body" || data.column.index !== 6) return;
        const sc = STATUS_COLORS[(apps[data.row.index].status || "").toLowerCase()];
        if (!sc) return;
        data.cell.styles.fillColor = sc.bg;
        data.cell.styles.textColor = sc.fg;
      },
    });
    cur.y = doc.lastAutoTable.finalY + 3;

    cur.space(9);
    doc.setFillColor(...C.zebra);
    doc.setDrawColor(...C.line);
    doc.setLineWidth(0.2);
    doc.rect(M, cur.y, CONTENT_W, 8, "FD");
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8.5);
    doc.setTextColor(...C.text);
    doc.text(
      `Total position${multi ? "s" : ""} applied for: ${apps.length}${
        multi ? "  ·  each application is assessed independently." : ""
      }`,
      M + 3.5,
      cur.y + 5.2,
    );
    cur.gap(11);
  }

  /* Section C — Cover letter */
  const coverLetter = latest("coverLetter");
  cur.sectionLabel("Section C — Cover Letter");
  cur.box(coverLetter || "No cover letter was submitted.", {
    empty: !coverLetter,
  });
  cur.gap(3);

  /* Section D — Declaration */
  cur.sectionLabel("Section D — Declaration");
  cur.paragraph(
    "I confirm that the information provided in this form is true, complete, and accurate to the best " +
      "of my knowledge. I understand that any false or misleading statement may result in my application " +
      "being withdrawn or, if already engaged, my engagement being terminated. I consent to QuantumLogics " +
      `processing the details above for the purpose of assessing my application${multi ? "s" : ""}.`,
  );
  cur.signatureRow();

  drawFooter(doc, ref);

  const date = new Date().toISOString().slice(0, 10);
  doc.save(
    `QuantumLogics_Registration_Form_${safeFileName(user?.name)}_${date}.pdf`,
  );
  return apps.length;
}

/**
 * Build and download the candidate's registration form as a PDF.
 *
 * Pulls every application on record (so applying for multiple positions lists
 * them all) plus the job catalogue for department/type/location detail.
 *
 * @param {object} user           Logged-in user ({ _id, name, email }).
 * @param {Array}  [applications] Pre-loaded applications; fetched when omitted.
 * @returns {Promise<number>} number of positions included in the form
 */
export async function openRegistrationForm(user, applications) {
  let apps = applications;
  if (!apps) {
    const { data } = await api.get("/applications/mine");
    apps = data;
  }

  // Job details are decorative — never let a failure block the form.
  let jobs = [];
  try {
    const { data } = await api.get("/jobs");
    jobs = data;
  } catch {
    jobs = [];
  }

  return buildPDF({ user, applications: apps || [], jobs });
}

export default openRegistrationForm;
